import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

const CONF_LEVELS = [
  { pct: 70, z: 1.0364 }, { pct: 75, z: 1.1503 }, { pct: 80, z: 1.2816 },
  { pct: 85, z: 1.4395 }, { pct: 90, z: 1.6449 }, { pct: 95, z: 1.9600 },
  { pct: 98, z: 2.3263 }, { pct: 99, z: 2.5758 }, { pct: 99.9, z: 3.2905 },
  { pct: 99.99, z: 3.8906 }, { pct: 99.999, z: 4.4172 },
];

export default function ConfidenceIntervalCalc() {
  const [mean, setMean] = useState(50);
  const [sd, setSd] = useState(10);
  const [n, setN] = useState(30);
  const [conf, setConf] = useState(95);
  const [showWork, setShowWork] = useState(false);
  const c = useMemo(() => {
    const z = (CONF_LEVELS.find(l => l.pct === conf) || { z: 1.96 }).z;
    const se = sd/Math.sqrt(n);
    return { z, moe: z*se, low: mean - z*se, high: mean + z*se, se };
  }, [mean, sd, n, conf]);
  return (
    <CalcShell look="cnet" title="Confidence Interval Calculator" subtitle="Compute a confidence interval for a sample mean, plus an error-bar visualization.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Sample Mean (x̄)</label><input type="number" step="any" value={mean} onChange={e => setMean(+e.target.value || 0)} data-testid="ci-mean"/></div>
          <div className="field"><label>Standard Deviation (σ or s)</label><input type="number" step="any" value={sd} onChange={e => setSd(+e.target.value || 0)} data-testid="ci-sd"/></div>
          <div className="field"><label>Sample Size (n)</label><input type="number" value={n} onChange={e => setN(+e.target.value || 0)} data-testid="ci-n"/></div>
          <div className="field"><label>Confidence Level (%)</label>
            <select value={conf} onChange={e => setConf(+e.target.value)} data-testid="ci-conf">
              {CONF_LEVELS.map(l => <option key={l.pct} value={l.pct}>{l.pct}% (z ≈ {l.z})</option>)}
            </select>
          </div>
        </div>
        <div>
          <div className="result-panel mb-4">
            <div className="result-title">{conf}% Confidence Interval</div>
            <div className="result-value" data-testid="ci-result">[{c.low.toFixed(4)}, {c.high.toFixed(4)}]</div>
            <div className="text-sm text-slate-600 mt-1">x̄ ± {c.moe.toFixed(4)}</div>
          </div>
          <table className="data-table"><tbody>
            <tr><td>Z-score</td><td className="text-right font-mono">{c.z}</td></tr>
            <tr><td>Standard error (σ/√n)</td><td className="text-right">{c.se.toFixed(4)}</td></tr>
            <tr><td>Margin of error</td><td className="text-right font-semibold">{c.moe.toFixed(4)}</td></tr>
            <tr><td>Lower bound</td><td className="text-right">{c.low.toFixed(4)}</td></tr>
            <tr><td>Upper bound</td><td className="text-right">{c.high.toFixed(4)}</td></tr>
          </tbody></table>
        </div>
      </div>

      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Error Bar</h3>
        <ErrorBar mean={mean} low={c.low} high={c.high} moe={c.moe} conf={conf}/>
      </div>

      <div className="mt-4">
        <button onClick={() => setShowWork(v => !v)} data-testid="ci-show-work-toggle"
          className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
          {showWork ? '− Hide working' : '+ Show working'}
        </button>
        {showWork && (
          <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="ci-work-panel">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
            <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
              <li>Look up the <b>Z-score</b> for {conf}%: <span className="font-mono">z = {c.z}</span></li>
              <li><b>Standard error of the mean</b>: <span className="font-mono">SE = σ / √n = {sd} / √{n} = <b>{c.se.toFixed(6)}</b></span></li>
              <li><b>Margin of error</b>: <span className="font-mono">MoE = z × SE = {c.z} × {c.se.toFixed(4)} = <b>{c.moe.toFixed(6)}</b></span></li>
              <li><b>Confidence interval</b>: <span className="font-mono">x̄ ± MoE = {mean} ± {c.moe.toFixed(4)}</span></li>
              <li><b>Lower bound</b> = {mean} − {c.moe.toFixed(4)} = <b>{c.low.toFixed(4)}</b></li>
              <li><b>Upper bound</b> = {mean} + {c.moe.toFixed(4)} = <b>{c.high.toFixed(4)}</b></li>
              <li>We are {conf}% confident the true population mean lies between <b className="text-[#4c7b25]">[{c.low.toFixed(4)}, {c.high.toFixed(4)}]</b>.</li>
            </ol>
          </div>
        )}
      </div>
    </CalcShell>
  );
}

function ErrorBar({ mean, low, high, moe, conf }) {
  // paint an axis with the mean and error bars, plus ±3σ range around it
  const spread = Math.max(1e-9, moe * 3);
  const axisMin = mean - spread;
  const axisMax = mean + spread;
  const w = 720, h = 130, padL = 40, padR = 40;
  const x = v => padL + ((v - axisMin) / (axisMax - axisMin)) * (w - padL - padR);
  const midY = h / 2;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
      <line x1={padL} y1={midY} x2={w - padR} y2={midY} stroke="#94a3b8" strokeWidth="2" />
      {/* Error bar */}
      <line x1={x(low)} y1={midY} x2={x(high)} y2={midY} stroke="#2563eb" strokeWidth="5" />
      <line x1={x(low)} y1={midY - 18} x2={x(low)} y2={midY + 18} stroke="#2563eb" strokeWidth="3" />
      <line x1={x(high)} y1={midY - 18} x2={x(high)} y2={midY + 18} stroke="#2563eb" strokeWidth="3" />
      {/* Mean point */}
      <circle cx={x(mean)} cy={midY} r="7" fill="#f97316" stroke="#fff" strokeWidth="2" />
      {/* Labels */}
      <text x={x(low)} y={midY + 40} textAnchor="middle" fontSize="12" fill="#0f172a">{low.toFixed(3)}</text>
      <text x={x(high)} y={midY + 40} textAnchor="middle" fontSize="12" fill="#0f172a">{high.toFixed(3)}</text>
      <text x={x(mean)} y={midY - 26} textAnchor="middle" fontSize="12" fill="#0f172a" fontWeight="600">x̄ = {mean}</text>
      <text x={w/2} y={h - 4} textAnchor="middle" fontSize="11" fill="#334155">{conf}% CI (±{moe.toFixed(4)})</text>
    </svg>
  );
}
